'use client';

import React, { useEffect, useState } from 'react';

import { Box, Stack, Flex, useDisclosure, useBreakpointValue } from '@chakra-ui/react';
import { useRouter } from 'next/navigation';

import { PrimaryButton } from '@components/custom-buttons';
import { CustomModal } from '@components/custom-modal';
import { CustomText } from '@components/Custom-text';
import { UserType } from '@enums/index';
import { useAppDispatch, useAppSelector } from '@hooks/store';
import useAuthSelector from '@hooks/useAuthSelector';
import { setLogout } from '@modules/auth/lib/authSlice';
import { Mentor } from '@modules/auth/types';
import { useGetServicesQuery } from '@modules/common/api';
import { useBookSessionMutation } from '@modules/find-mentor/api';
import { SlotToBook } from '@modules/find-mentor/types';
import usePeronalInfo from '@modules/profile/hooks/usePeronalInfo';
import { useGetMeteePlansQuery } from '@modules/billings/api';
import { paths } from '@routes/path';

import { addBookSessionData } from '../../slice/bookingSuccessSlice';
import BookSessionCard from '../cards/BookSessionCard';
import ServicesForm from '../forms/ServicesForm';
import PreviewProfileBody from '../sections/PreviewProfileBody';
import { usePagination } from '@ajna/pagination';
import { MenteesPlanStatus } from '@modules/billings/enums';

type BookSessionViewProps = {
    slug: string;
};

const BookSessionView = ({ slug }: BookSessionViewProps) => {
    const [showServicesForm, setShowServicesForm] = useState<boolean>(false);
    const [totalRecords, setTotalRecords] = useState(0);
    const { isAuthenticated, user } = useAuthSelector();
    const { isOpen, onClose, onOpen } = useDisclosure();
    const {
        isOpen: isMobileModalOpen,
        onOpen: openMobileModal,
        onClose: closeMobileModal,
    } = useDisclosure();
    const [selectedSlot, setSelectedSlot] = useState<SlotToBook>();
    const router = useRouter();
    const dispatch = useAppDispatch();

    const isLgOrAbove = useBreakpointValue({ base: false, lg: true });

    const menteeName = useAppSelector(state => state.reducer.auth.user?.name);
    const shouldBook = useAppSelector(state => state.reducer.booking.shouldBook);

    // apis
    const { data } = useGetServicesQuery({});
    const { getPersonalInfo, isLoading, personalProfileInfo } = usePeronalInfo({
        userType: UserType.Mentor,
    });

    const { offset, pageSize } = usePagination({
        total: totalRecords,
        limits: {
            outer: 2,
            inner: 2,
        },
        initialState: {
            pageSize: 4,
            currentPage: 1,
        },
    });

    const {
        data: plansData,
        isFetching,
        isSuccess,
    } = useGetMeteePlansQuery(
        {
            offset,
            limit: pageSize,
            status: MenteesPlanStatus.Active,
        },
        {
            skip: !isAuthenticated,
        }
    );

    const handleConfirmation = () => {
        if (isAuthenticated) {
            dispatch(setLogout());
            router.replace(paths.mentee.auth.signup);
        } else {
            router.replace(paths.mentee.auth.signup);
        }
    };

    const handleNext = () => {
        // if (!isAuthenticated || user?.userType === UserType.Mentor) {
        //     onOpen();
        //     return;
        // }
        setShowServicesForm(true);
    };

    useEffect(() => {
        getPersonalInfo(slug);
    }, [getPersonalInfo, slug]);

    const [
        handleBookSession,
        { isLoading: bookSessionLoading, isSuccess: bookSessionSuccess, data: bookSessionData },
    ] = useBookSessionMutation();

    useEffect(() => {
        if (bookSessionSuccess && !bookSessionLoading && bookSessionData) {
            dispatch(
                addBookSessionData({
                    imageUrl: bookSessionData?.mentor?.profileImageUrl,
                    fromDate: bookSessionData?.startDate,
                    toDate: bookSessionData?.endDate,
                    service: bookSessionData?.service?.type,
                    showModel: true,
                    mentorName: bookSessionData?.mentor?.name,
                })
            );
            router.replace(paths.mentee.dashboard.bookings);
        }
    }, [bookSessionLoading, bookSessionSuccess, router, bookSessionData, dispatch]);

    useEffect(() => {
        if (isSuccess) setTotalRecords(plansData?.count);
    }, [isSuccess, plansData?.count]);

    useEffect(() => {
        if (shouldBook) {
            setShowServicesForm(true);
        }
    }, [shouldBook]);

    return (
        <>
            <Box
                py={5}
                px={{ base: 0, sm: 1, md: 3, lg: 5, xl: 0 }}
                maxWidth={{ md: '1200px', xl: '1300px' }}
                mx="auto"
            >
                <Stack
                    flexDir={{ base: 'column', sm: 'column', md: 'row' }}
                    width="100%"
                    height="max-content"
                    gap={5}
                    align={{ base: 'flex-end', md: 'flex-start' }}
                    justify="space-between"
                >
                    {!isLgOrAbove && (
                        <Box
                            position="fixed"
                            bottom="0"
                            left="0"
                            width="100%"
                            bgColor="white"
                            py={4}
                            px={4}
                            zIndex="1000"
                            boxShadow="0 -2px 6px rgba(0, 0, 0, 0.1)"
                        >
                            <Flex justify="space-between" align="center">
                                <Flex>
                                    <CustomText color="mainText" size="xl" weight={400}>
                                        Book a session:
                                    </CustomText>
                                </Flex>

                                <PrimaryButton
                                    type="button"
                                    buttonLabel="Book Session"
                                    onClick={openMobileModal}
                                    width="50%"
                                />
                            </Flex>
                        </Box>
                    )}
                    <PreviewProfileBody
                        slug={slug}
                        personalProfileInfo={personalProfileInfo as Mentor}
                        isLoading={isLoading}
                        user={user}
                    />
                    {isLgOrAbove &&
                        (showServicesForm && !isLoading && personalProfileInfo?.id ? (
                            <Box width="40%" bgColor="headerBg" rounded={6} p={5}>
                                <ServicesForm
                                    isLoading={bookSessionLoading}
                                    onSubmit={handleBookSession}
                                    onCancel={() => setShowServicesForm(false)}
                                    onOpenDialog={onOpen}
                                    isNotAuthenticated={
                                        !isAuthenticated || user?.userType === UserType.Mentor
                                    }
                                    services={data?.servicesArray!}
                                    selectedSlot={selectedSlot}
                                    mentorId={personalProfileInfo?.id}
                                    mentorName={personalProfileInfo?.name}
                                    menteeName={menteeName || ''}
                                    plansData={plansData}
                                />
                            </Box>
                        ) : (
                            <BookSessionCard
                                slug={slug}
                                setSelectedSlot={setSelectedSlot}
                                onNext={handleNext}
                            />
                        ))}
                </Stack>
            </Box>
            <CustomModal
                confirmBtnText="Sign Up"
                cancelBtnText="Cancel"
                cancelBtnColorScheme="gray"
                confirmBtnColorScheme="blue"
                isOpen={isOpen}
                onClose={onClose}
                onConfirmation={handleConfirmation}
                onCancel={onClose}
                title={null}
                body="Please Sign up as a Mentee to book a session"
                isLoading={false}
                bodyFontSize="2xl"
                hideCloseBtn
                textAlign="center"
                showIcon
                cancelBtnTextColor="mainText"
            />
            <CustomModal
                isOpen={isMobileModalOpen}
                onClose={closeMobileModal}
                onCancel={closeMobileModal}
                onConfirmation={() => {}}
                confirmBtnText=""
                cancelBtnText="Close"
                cancelBtnColorScheme="gray"
                confirmBtnColorScheme="white"
                hideCancelBtn
                isLoading={false}
                title={null}
                body={
                    showServicesForm && !isLoading && personalProfileInfo?.id ? (
                        <Box width="100%" bgColor="headerBg" rounded={6} p={3}>
                            <ServicesForm
                                isLoading={bookSessionLoading}
                                onSubmit={handleBookSession}
                                onCancel={() => setShowServicesForm(false)}
                                onOpenDialog={onOpen}
                                isNotAuthenticated={
                                    !isAuthenticated || user?.userType === UserType.Mentor
                                }
                                services={data?.servicesArray!}
                                selectedSlot={selectedSlot}
                                mentorId={personalProfileInfo.id || ''}
                                mentorName={personalProfileInfo.name}
                                menteeName={menteeName || ''}
                                plansData={plansData}
                            />
                        </Box>
                    ) : (
                        <BookSessionCard
                            slug={slug}
                            setSelectedSlot={setSelectedSlot}
                            onNext={() => {
                                if (!isAuthenticated || user?.userType === UserType.Mentor) {
                                    onOpen();
                                    return;
                                }
                                setShowServicesForm(true);
                            }}
                        />
                    )
                }
                size="md"
                hideCloseBtn={false}
                bodyFontSize="md"
                textAlign="left"
                mt={{ base: '40px', md: 'auto' }}
            />
        </>
    );
};

export default BookSessionView;
